/**
 * Page Employés — Ressources Humaines
 * Architecture : liste à gauche, panneau détail à droite
 * Onglets : Identité | Contrat de travail
 * Conforme ARCHITECTURE.md + charte graphique Bedee (CSS vars)
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  User, Plus, Search, X, ChevronRight, Building2, Calendar,
  Lock, Clock, Edit2, Trash2, Check, AlertTriangle, Save,
  RefreshCw, FileText, Briefcase,
} from 'lucide-react';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ─── Fonctions internes (liste fixe) ─────────────────────────────────────────

const FONCTIONS_INTERNES = [
  'Technicien atelier',
  'Agent de contrôle',
  'Chauffeur',
  'Chef de dépôt',
  'Commercial',
  'Responsable technique',
];

const FI_COULEURS = {
  'Technicien atelier':   { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' },
  'Agent de contrôle':    { bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' },
  'Chauffeur':            { bg: '#FFF7ED', color: '#EA580C', border: '#FED7AA' },
  'Chef de dépôt':        { bg: '#F5F3FF', color: '#7C3AED', border: '#DDD6FE' },
  'Commercial':           { bg: '#ECFEFF', color: '#0891B2', border: '#A5F3FC' },
  'Responsable technique':{ bg: '#FFFBEB', color: '#B45309', border: '#FDE68A' },
};

// ─── Constantes ───────────────────────────────────────────────────────────────

const STATUTS = [
  { val: 'actif',    label: 'Actif',    color: '#16A34A', bg: '#DCFCE7' },
  { val: 'inactif',  label: 'Inactif',  color: '#6B7280', bg: '#F3F4F6' },
  { val: 'conge',    label: 'En congé', color: '#D97706', bg: '#FEF3C7' },
  { val: 'rupture',  label: 'Rupture',  color: '#DC2626', bg: '#FEE2E2' },
];
const TYPES_CONTRAT = ['CDI', 'CDD', 'interim', 'alternance', 'stage'];
const METHODES = [
  { val: 'or_atelier',  label: 'Pointage atelier (OR + TNF)' },
  { val: 'pointeuse',   label: 'Pointeuse (à venir)' },
  { val: 'teletravail', label: 'Télétravail (à venir)' },
];
const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const JOURS_COURTS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function statutInfo(val) { return STATUTS.find(s => s.val === val) || STATUTS[1]; }

// ─── Calcul heures hebdo ──────────────────────────────────────────────────────

function diffH(t1, t2) {
  if (!t1 || !t2) return 0;
  const [h1, m1] = t1.split(':').map(Number);
  const [h2, m2] = t2.split(':').map(Number);
  return Math.max(0, (h2 * 60 + m2 - h1 * 60 - m1) / 60);
}

function calcHebdo(horaires) {
  if (!horaires) return 0;
  return JOURS.reduce((sum, j) => {
    const d = horaires[j];
    if (!d) return sum;
    return sum + diffH(d.matin_debut, d.matin_fin) + diffH(d.am_debut, d.am_fin);
  }, 0);
}

function fmtH(h) {
  if (!h && h !== 0) return '—';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}h${mm > 0 ? String(mm).padStart(2, '0') : ''}`;
}

// ─── Badge statut ─────────────────────────────────────────────────────────────

function StatutBadge({ val }) {
  const s = statutInfo(val);
  return (
    <span style={{
      fontSize: '0.66rem', fontWeight: 700, padding: '2px 8px',
      borderRadius: 20, background: s.bg, color: s.color,
      letterSpacing: '0.02em',
    }}>
      {s.label}
    </span>
  );
}

// ─── Badges fonctions internes ────────────────────────────────────────────────

function FonctionBadge({ fonc, small = false }) {
  const c = FI_COULEURS[fonc] || { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' };
  return (
    <span style={{
      fontSize: small ? '0.62rem' : '0.68rem',
      fontWeight: 600,
      padding: small ? '1px 6px' : '2px 8px',
      borderRadius: 20,
      background: c.bg, color: c.color,
      border: `1px solid ${c.border}`,
      whiteSpace: 'nowrap',
    }}>
      {fonc}
    </span>
  );
}

// ─── Sélecteur multi-fonctions internes ───────────────────────────────────────

function FonctionsInternesSelector({ value = [], onChange }) {
  const toggle = (f) => {
    if (value.includes(f)) onChange(value.filter(x => x !== f));
    else onChange([...value, f]);
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
      {FONCTIONS_INTERNES.map(f => {
        const isOn = value.includes(f);
        const c = FI_COULEURS[f] || { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' };
        return (
          <button
            key={f}
            type="button"
            onClick={() => toggle(f)}
            data-testid={`fi-toggle-${f.replace(/ /g, '-')}`}
            style={{
              fontSize: '0.75rem', fontWeight: 600,
              padding: '4px 11px', borderRadius: 20, cursor: 'pointer',
              border: `1.5px solid ${isOn ? c.border : 'var(--border)'}`,
              background: isOn ? c.bg : 'white',
              color: isOn ? c.color : 'var(--text-muted)',
              transition: 'all 0.12s',
            }}
          >
            {isOn && <Check size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
            {f}
          </button>
        );
      })}
    </div>
  );
}

// ─── Grille horaires hebdomadaires ────────────────────────────────────────────

function GrilleHoraires({ horaires, onChange, readOnly = false }) {
  const h = horaires || {};
  const hebdo = useMemo(() => calcHebdo(h), [h]);

  const setField = (jour, field, val) => {
    const next = { ...h, [jour]: { ...(h[jour] || {}), [field]: val || null } };
    onChange(next);
  };

  const toggleJour = (jour) => {
    if (h[jour]) {
      const next = { ...h };
      delete next[jour];
      onChange(next);
    } else {
      onChange({ ...h, [jour]: { matin_debut: '08:00', matin_fin: '12:00', am_debut: '13:30', am_fin: '17:30' } });
    }
  };

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-subtle)' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', width: 30 }} />
              {JOURS_COURTS.map((j, i) => {
                const jour = JOURS[i];
                const actif = !!h[jour];
                const quota = actif ? diffH(h[jour]?.matin_debut, h[jour]?.matin_fin) + diffH(h[jour]?.am_debut, h[jour]?.am_fin) : 0;
                return (
                  <th key={j} style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: actif ? 'var(--text)' : 'var(--text-disabled)', borderBottom: '1px solid var(--border)', minWidth: 90 }}>
                    {!readOnly ? (
                      <button onClick={() => toggleJour(jour)} style={{
                        background: actif ? 'var(--primary-solid)' : 'var(--bg-subtle)',
                        color: actif ? 'white' : 'var(--text-muted)',
                        border: 'none', borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
                        fontSize: '0.72rem', fontWeight: 700,
                      }}>
                        {j}
                      </button>
                    ) : (
                      <span style={{ color: actif ? 'var(--text)' : 'var(--text-disabled)' }}>{j}</span>
                    )}
                    {actif && <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: 2 }}>{fmtH(quota)}</div>}
                  </th>
                );
              })}
              <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--text)', borderBottom: '1px solid var(--border)' }}>
                Total<br /><span style={{ color: 'var(--primary-solid)' }}>{fmtH(hebdo)}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Matin début', field: 'matin_debut' },
              { label: 'Matin fin',   field: 'matin_fin' },
              { label: 'AM début',    field: 'am_debut' },
              { label: 'AM fin',      field: 'am_fin' },
            ].map(row => (
              <tr key={row.field}>
                <td style={{ padding: '4px 8px', fontSize: '0.68rem', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)', whiteSpace: 'nowrap' }}>{row.label}</td>
                {JOURS.map(jour => {
                  const actif = !!h[jour];
                  const val = h[jour]?.[row.field] || '';
                  return (
                    <td key={jour} style={{ padding: '3px 4px', textAlign: 'center', borderBottom: '1px solid var(--border-light)' }}>
                      {actif ? (
                        readOnly ? (
                          <span style={{ fontSize: '0.78rem', color: 'var(--text)' }}>{val || '—'}</span>
                        ) : (
                          <input type="time" value={val} onChange={e => setField(jour, row.field, e.target.value)}
                            style={{ width: 80, padding: '3px 4px', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.78rem', textAlign: 'center' }} />
                        )
                      ) : (
                        <span style={{ color: 'var(--text-disabled)', fontSize: '0.72rem' }}>—</span>
                      )}
                    </td>
                  );
                })}
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 8 }}>
          Cliquez sur un jour pour l'activer / désactiver.
        </p>
      )}
    </div>
  );
}

// ─── Formulaire contrat de travail ────────────────────────────────────────────

function ContratTravailForm({ employeId, contrat, onSaved, onCancel }) {
  const isNew = !contrat;
  const [form, setForm] = useState({
    type_contrat: contrat?.type_contrat || 'CDI',
    date_debut:   contrat?.date_debut   || new Date().toISOString().slice(0, 10),
    date_fin:     contrat?.date_fin     || '',
    methode_pointage: contrat?.methode_pointage || 'or_atelier',
    horaires:     contrat?.horaires     || {
      lundi:    { matin_debut: '08:00', matin_fin: '12:00', am_debut: '13:30', am_fin: '17:30' },
      mardi:    { matin_debut: '08:00', matin_fin: '12:00', am_debut: '13:30', am_fin: '17:30' },
      mercredi: { matin_debut: '08:00', matin_fin: '12:00', am_debut: '13:30', am_fin: '17:30' },
      jeudi:    { matin_debut: '08:00', matin_fin: '12:00', am_debut: '13:30', am_fin: '17:30' },
      vendredi: { matin_debut: '08:00', matin_fin: '12:00', am_debut: '13:30', am_fin: '17:30' },
    },
    notes: contrat?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const hebdo = useMemo(() => calcHebdo(form.horaires), [form.horaires]);

  const save = async () => {
    if (!form.date_debut) { toast.error('Date de début obligatoire'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.date_fin) delete payload.date_fin;
      if (!payload.notes) delete payload.notes;
      if (isNew) {
        await api.addContratTravail(employeId, payload);
        toast.success('Contrat de travail créé');
      } else {
        await api.updateContratTravail(contrat.contrat_travail_id, payload);
        toast.success('Contrat mis à jour');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border p-4 mb-3" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 130 }}>
          <label style={LBL}>Type de contrat</label>
          <select value={form.type_contrat} onChange={e => setForm(p => ({ ...p, type_contrat: e.target.value }))} style={SEL}>
            {TYPES_CONTRAT.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 130 }}>
          <label style={LBL}>Date de début <span style={{ color: '#EF4444' }}>*</span></label>
          <input type="date" value={form.date_debut} onChange={e => setForm(p => ({ ...p, date_debut: e.target.value }))} style={INP} />
        </div>
        <div style={{ flex: 1, minWidth: 130 }}>
          <label style={LBL}>Date de fin</label>
          <input type="date" value={form.date_fin || ''} onChange={e => setForm(p => ({ ...p, date_fin: e.target.value || '' }))} min={form.date_debut} style={INP} />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={LBL}>Méthode de pointage</label>
        <select value={form.methode_pointage} onChange={e => setForm(p => ({ ...p, methode_pointage: e.target.value }))} style={SEL}>
          {METHODES.map(m => <option key={m.val} value={m.val}>{m.label}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ ...LBL, marginBottom: 8 }}>
          Horaires de travail —{' '}
          <span style={{ color: 'var(--primary-solid)', fontWeight: 700 }}>Base : {fmtH(hebdo)} / sem.</span>
        </label>
        <GrilleHoraires horaires={form.horaires} onChange={h => setForm(p => ({ ...p, horaires: h }))} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={LBL}>Notes</label>
        <textarea value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
          rows={2} style={{ ...INP, resize: 'vertical' }} placeholder="Notes..." />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={BTN_SEC} disabled={saving}>Annuler</button>
        <button onClick={save} disabled={saving} style={BTN_PRI} data-testid="ct-save-btn">
          {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
          {isNew ? 'Créer le contrat' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

// ─── Panel détail employé ─────────────────────────────────────────────────────

function EmployePanel({ employeId, onClose, onUpdated }) {
  const [employe, setEmploye] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('identite');
  const [editing, setEditing] = useState(false);
  const [addingCT, setAddingCT] = useState(false);
  const [editCT, setEditCT] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [agences, setAgences] = useState([]);

  useEffect(() => {
    api.getAgences().then(r => setAgences(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getEmploye(employeId);
      setEmploye(r.data);
      setForm({
        matricule:           r.data.matricule || '',
        date_embauche:       r.data.date_embauche || '',
        date_naissance:      r.data.date_naissance || '',
        num_secu_sociale:    r.data.num_secu_sociale || '',
        salaire_base:        r.data.salaire_base ?? '',
        note_confidentielle: r.data.note_confidentielle || '',
        agence_id:           r.data.agence_id || '',
        societe_id:          r.data.societe_id || '',
        statut:              r.data.statut || 'actif',
        fonctions_internes:  r.data.fonctions_internes || [],
        odoo_user_id:        r.data.odoo_user_id ?? '',
      });
    } catch {
      toast.error('Impossible de charger la fiche employé');
    } finally {
      setLoading(false);
    }
  }, [employeId]);

  useEffect(() => { load(); }, [load]);

  const saveIdentite = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.salaire_base && payload.salaire_base !== 0) delete payload.salaire_base;
      if (!payload.date_embauche) delete payload.date_embauche;
      if (!payload.date_naissance) delete payload.date_naissance;
      if (!payload.agence_id) delete payload.agence_id;
      if (!payload.societe_id) delete payload.societe_id;
      if (!payload.num_secu_sociale) delete payload.num_secu_sociale;
      if (payload.odoo_user_id === '' || payload.odoo_user_id === null) delete payload.odoo_user_id;
      else if (payload.odoo_user_id) payload.odoo_user_id = parseInt(payload.odoo_user_id, 10);
      await api.updateEmploye(employeId, payload);
      toast.success('Fiche mise à jour');
      setEditing(false);
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <RefreshCw size={20} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
    </div>
  );
  if (!employe) return null;

  const { personne = {}, contrats_travail = [] } = employe;
  const ctActif = contrats_travail.find(c => c.actif);
  const nomComplet = `${personne.prenom || ''} ${personne.nom || ''}`.trim() || employe.employe_id;
  const foncsInternes = employe.fonctions_internes || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header panel */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--primary-solid)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'white', marginBottom: 4 }}>{nomComplet}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <StatutBadge val={employe.statut} />
            {employe.matricule && (
              <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)' }}>#{employe.matricule}</span>
            )}
            {foncsInternes.length > 0 && foncsInternes.slice(0, 2).map(f => (
              <span key={f} style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.15)', padding: '1px 7px', borderRadius: 20 }}>
                {f}
              </span>
            ))}
            {foncsInternes.length > 2 && (
              <span style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.6)' }}>+{foncsInternes.length - 2}</span>
            )}
          </div>
        </div>
        <button onClick={onClose} style={{ color: 'rgba(255,255,255,0.7)', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, flexShrink: 0 }}>
          <X size={18} />
        </button>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'white' }}>
        {[
          { key: 'identite', label: 'Identité' },
          { key: 'contrat',  label: 'Contrat de travail' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            style={{
              padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '0.82rem', fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? 'var(--primary-solid)' : 'var(--text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--primary-solid)' : '2px solid transparent',
            }}>{t.label}</button>
        ))}
      </div>

      {/* Corps */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

        {/* ── Identité ── */}
        {tab === 'identite' && (
          <>
            {/* Carte personne liée */}
            <div style={{ padding: '10px 14px', background: 'var(--primary-50)', borderRadius: 10, border: '1px solid var(--primary-100)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <User size={16} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary-solid)' }}>{nomComplet}</p>
                {personne.email && <p style={{ fontSize: '0.68rem', color: 'var(--primary-light)', marginTop: 1 }}>{personne.email}</p>}
                {personne.telephone_mobile && <p style={{ fontSize: '0.68rem', color: 'var(--primary-light)' }}>{personne.telephone_mobile}</p>}
              </div>
              <span style={{ fontSize: '0.62rem', color: 'var(--primary-solid)', background: 'var(--primary-100)', padding: '2px 6px', borderRadius: 4 }}>Annuaire</span>
            </div>

            {!editing ? (
              <>
                {/* Infos clés */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                  {[
                    { label: 'Matricule',     val: employe.matricule },
                    { label: 'Date embauche', val: employe.date_embauche },
                    { label: 'Statut',        val: <StatutBadge val={employe.statut} /> },
                    { label: 'Agence',        val: agences.find(a => a.agence_id === employe.agence_id)?.nom || employe.agence_id },
                  ].map(f => (
                    <div key={f.label} style={{ padding: '8px 12px', background: 'var(--bg-subtle)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <p style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f.label}</p>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text)', fontWeight: 500 }}>{f.val || '—'}</p>
                    </div>
                  ))}
                </div>

                {/* Fonctions internes */}
                <div style={{ padding: '12px 14px', background: 'var(--bg-subtle)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <Briefcase size={13} style={{ color: 'var(--primary-solid)' }} />
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--primary-solid)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Fonctions internes
                    </span>
                  </div>
                  {foncsInternes.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {foncsInternes.map(f => <FonctionBadge key={f} fonc={f} />)}
                    </div>
                  ) : (
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      Aucune fonction interne attribuée
                    </p>
                  )}
                </div>

                {/* Données confidentielles */}
                <div style={{ padding: '12px 14px', background: '#FFF8F0', borderRadius: 10, border: '1px solid #FED7AA', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <Lock size={13} style={{ color: '#D97706' }} />
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Données confidentielles</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                      { label: 'Date naissance',   val: employe.date_naissance },
                      { label: 'N° Sécu sociale',  val: employe.num_secu_sociale ? '••• ••• ••• ••• ••' : '—' },
                      { label: 'Salaire de base',  val: employe.salaire_base != null ? `${employe.salaire_base.toLocaleString('fr-FR')} €` : '—' },
                    ].map(f => (
                      <div key={f.label}>
                        <p style={{ fontSize: '0.62rem', color: '#9CA3AF', marginBottom: 2 }}>{f.label}</p>
                        <p style={{ fontSize: '0.82rem', color: '#374151' }}>{f.val || '—'}</p>
                      </div>
                    ))}
                  </div>
                  {employe.note_confidentielle && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #FED7AA' }}>
                      <p style={{ fontSize: '0.68rem', color: '#9CA3AF', marginBottom: 2 }}>Note confidentielle</p>
                      <p style={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'pre-wrap' }}>{employe.note_confidentielle}</p>
                    </div>
                  )}
                </div>

                <button onClick={() => setEditing(true)} style={BTN_SEC} data-testid="edit-employe-btn">
                  <Edit2 size={13} /> Modifier la fiche
                </button>
              </>
            ) : (
              /* Formulaire édition */
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div>
                    <label style={LBL}>Matricule</label>
                    <input value={form.matricule} onChange={e => setForm(p => ({ ...p, matricule: e.target.value }))} style={INP} placeholder="EMP001" maxLength={50} />
                  </div>
                  <div>
                    <label style={LBL}>Date embauche</label>
                    <input type="date" value={form.date_embauche} onChange={e => setForm(p => ({ ...p, date_embauche: e.target.value }))} style={INP} />
                  </div>
                  <div>
                    <label style={LBL}>Statut</label>
                    <select value={form.statut} onChange={e => setForm(p => ({ ...p, statut: e.target.value }))} style={SEL}>
                      {STATUTS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={LBL}>Agence</label>
                    <select value={form.agence_id} onChange={e => setForm(p => ({ ...p, agence_id: e.target.value }))} style={SEL}>
                      <option value="">— Non assigné —</option>
                      {agences.map(a => (
                        <option key={a.agence_id} value={a.agence_id}>{a.nom}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={LBL}>ID utilisateur Odoo</label>
                    <input
                      type="number"
                      value={form.odoo_user_id}
                      onChange={e => setForm(p => ({ ...p, odoo_user_id: e.target.value }))}
                      style={INP}
                      placeholder="ex : 12"
                      min={1}
                      step={1}
                      data-testid="input-odoo-user-id"
                    />
                  </div>
                </div>

                {/* Fonctions internes */}
                <div style={{ marginBottom: 12, padding: '12px 14px', background: 'var(--bg-subtle)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <Briefcase size={13} style={{ color: 'var(--primary-solid)' }} />
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--primary-solid)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Fonctions internes
                    </span>
                    <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>— cliquez pour activer / désactiver</span>
                  </div>
                  <FonctionsInternesSelector
                    value={form.fonctions_internes || []}
                    onChange={v => setForm(p => ({ ...p, fonctions_internes: v }))}
                  />
                </div>

                <div style={{ padding: '12px 14px', background: '#FFF8F0', borderRadius: 10, border: '1px solid #FED7AA', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <Lock size={13} style={{ color: '#D97706' }} />
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#D97706', textTransform: 'uppercase' }}>Données confidentielles</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={LBL}>Date naissance</label>
                      <input type="date" value={form.date_naissance} onChange={e => setForm(p => ({ ...p, date_naissance: e.target.value }))} style={INP} />
                    </div>
                    <div>
                      <label style={LBL}>N° Sécu sociale</label>
                      <input value={form.num_secu_sociale} onChange={e => setForm(p => ({ ...p, num_secu_sociale: e.target.value }))} style={INP} placeholder="1 85 09 75 116 042 48" maxLength={50} />
                    </div>
                    <div>
                      <label style={LBL}>Salaire de base (€)</label>
                      <input type="number" value={form.salaire_base} onChange={e => setForm(p => ({ ...p, salaire_base: e.target.value }))} style={INP} min={0} step={0.01} />
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={LBL}>Note confidentielle</label>
                    <textarea value={form.note_confidentielle} onChange={e => setForm(p => ({ ...p, note_confidentielle: e.target.value }))}
                      rows={2} style={{ ...INP, resize: 'vertical' }} maxLength={1000} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setEditing(false)} style={BTN_SEC} disabled={saving}>Annuler</button>
                  <button onClick={saveIdentite} style={BTN_PRI} disabled={saving} data-testid="save-identite-btn">
                    {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                    Enregistrer
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Contrat de travail ── */}
        {tab === 'contrat' && (
          <>
            {ctActif && !editCT && !addingCT && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text)' }}>Contrat actif</span>
                    <span style={{ fontSize: '0.65rem', background: '#DCFCE7', color: '#16A34A', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>Actif</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditCT(ctActif)} style={{ ...BTN_SEC, padding: '4px 10px', fontSize: '0.72rem' }}>
                      <Edit2 size={11} /> Modifier
                    </button>
                    <button onClick={() => setAddingCT(true)} style={{ ...BTN_PRI, padding: '4px 10px', fontSize: '0.72rem' }}>
                      <Plus size={11} /> Nouveau contrat
                    </button>
                  </div>
                </div>
                <div style={{ padding: 14, background: 'var(--bg-subtle)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
                    {[
                      { label: 'Type', val: ctActif.type_contrat },
                      { label: 'Début', val: ctActif.date_debut },
                      { label: 'Fin', val: ctActif.date_fin || 'Indéterminée' },
                      { label: 'Pointage', val: METHODES.find(m => m.val === ctActif.methode_pointage)?.label || ctActif.methode_pointage },
                      { label: 'Base hebdo', val: <span style={{ color: 'var(--primary-solid)', fontWeight: 700 }}>{fmtH(ctActif.heures_hebdo_contractuelles)}</span> },
                    ].map(f => (
                      <div key={f.label}>
                        <p style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f.label}</p>
                        <p style={{ fontSize: '0.82rem', color: 'var(--text)', fontWeight: 500 }}>{f.val || '—'}</p>
                      </div>
                    ))}
                  </div>
                  <GrilleHoraires horaires={ctActif.horaires} onChange={() => {}} readOnly />
                </div>
              </div>
            )}

            {!ctActif && !addingCT && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
                <FileText size={28} style={{ marginBottom: 8 }} />
                <p style={{ fontSize: '0.85rem', marginBottom: 12 }}>Aucun contrat de travail actif</p>
                <button onClick={() => setAddingCT(true)} style={BTN_PRI} data-testid="add-ct-btn">
                  <Plus size={14} /> Créer un contrat de travail
                </button>
              </div>
            )}

            {(addingCT || editCT) && (
              <ContratTravailForm
                employeId={employeId}
                contrat={editCT}
                onSaved={async () => { setAddingCT(false); setEditCT(null); await load(); }}
                onCancel={() => { setAddingCT(false); setEditCT(null); }}
              />
            )}

            {contrats_travail.filter(c => !c.actif).length > 0 && (
              <div style={{ marginTop: 20 }}>
                <p style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Historique</p>
                {contrats_travail.filter(c => !c.actif).map(ct => (
                  <div key={ct.contrat_travail_id} style={{ padding: '8px 12px', background: 'var(--bg-subtle)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 6, opacity: 0.7 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)' }}>{ct.type_contrat}</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{ct.date_debut} → {ct.date_fin || '—'}</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--primary-solid)' }}>{fmtH(ct.heures_hebdo_contractuelles)} /sem.</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Modal nouvel employé ─────────────────────────────────────────────────────

function NouvelEmployeModal({ onClose, onCreated }) {
  const [q, setQ] = useState('');
  const [persons, setPersons] = useState([]);
  const [selectedP, setSelectedP] = useState(null);
  const [matricule, setMatricule] = useState('');
  const [dateEmbauche, setDateEmbauche] = useState(new Date().toISOString().slice(0, 10));
  const [statut, setStatut] = useState('actif');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setPersons([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.getPersonnes({ search: q, limit: 10 });
        setPersons(r.data?.items || r.data || []);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async () => {
    if (!selectedP) { toast.error('Sélectionnez une personne'); return; }
    setSaving(true);
    try {
      const r = await api.createEmploye({ personne_id: selectedP.personne_id, matricule, date_embauche: dateEmbauche, statut });
      toast.success('Employé créé');
      onCreated(r.data.employe_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1200 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 460, background: 'white', borderRadius: 14, zIndex: 1201,
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text)',  }}>Nouvel employé</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: '18px 20px' }}>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 14 }}>
            Un employé doit être rattaché à une personne existante dans l'annuaire.
          </p>

          <label style={LBL}>Personne de l'annuaire <span style={{ color: '#EF4444' }}>*</span></label>
          {selectedP ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--primary-50)', borderRadius: 8, border: '1px solid var(--primary-100)', marginBottom: 12 }}>
              <User size={13} style={{ color: 'var(--primary-solid)' }} />
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary-solid)', flex: 1 }}>
                {selectedP.prenom} {selectedP.nom} {selectedP.fonction ? `— ${selectedP.fonction}` : ''}
              </span>
              <button onClick={() => setSelectedP(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={12} /></button>
            </div>
          ) : (
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
                <Search size={13} style={{ color: 'var(--text-muted)' }} />
                <input data-testid="new-emp-search" value={q} onChange={e => setQ(e.target.value)}
                  placeholder="Rechercher nom, prénom..." style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.85rem' }} />
              </div>
              {persons.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', borderRadius: 8, border: '1px solid var(--border)', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 10 }}>
                  {persons.map(p => (
                    <button key={p.personne_id} onClick={() => { setSelectedP(p); setQ(''); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid var(--border-light)', cursor: 'pointer', fontSize: '0.83rem' }}>
                      <User size={12} style={{ color: 'var(--text-muted)' }} />
                      <span>{p.prenom} {p.nom}</span>
                      {p.fonction && <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{p.fonction}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Matricule</label>
              <input value={matricule} onChange={e => setMatricule(e.target.value)} style={INP} placeholder="EMP001" maxLength={50} />
            </div>
            <div>
              <label style={LBL}>Date d'embauche</label>
              <input type="date" value={dateEmbauche} onChange={e => setDateEmbauche(e.target.value)} style={INP} />
            </div>
            <div>
              <label style={LBL}>Statut</label>
              <select value={statut} onChange={e => setStatut(e.target.value)} style={SEL}>
                {STATUTS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={BTN_SEC}>Annuler</button>
          <button onClick={submit} disabled={!selectedP || saving} style={BTN_PRI} data-testid="create-employe-btn">
            {saving ? <RefreshCw size={13} className="animate-spin" /> : <Plus size={13} />}
            Créer l'employé
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Styles partagés ──────────────────────────────────────────────────────────

const LBL = { fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 };
const INP = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.83rem', outline: 'none', boxSizing: 'border-box', color: 'var(--text)', background: 'white' };
const SEL = { ...INP, background: 'white', cursor: 'pointer' };
const BTN_PRI = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', background: 'var(--primary-solid)', color: 'white',
  border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600,
};
const BTN_SEC = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: 'white', color: 'var(--text)',
  border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: '0.83rem',
};

// ─── Page principale ──────────────────────────────────────────────────────────

export default function Employes() {
  const { activeAgences } = useSociete();
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterFonction, setFilterFonction] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getEmployes({
        statut: filterStatut || undefined,
        fonction_interne: filterFonction || undefined,
      });
      setEmployes(r.data || []);
    } catch {
      toast.error('Impossible de charger les employés');
    } finally {
      setLoading(false);
    }
  }, [filterStatut, filterFonction, activeAgences]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search) return employes;
    const q = search.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return employes.filter(e => {
      const nom = `${e.personne?.prenom || ''} ${e.personne?.nom || ''} ${e.matricule || ''}`.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return nom.includes(q);
    });
  }, [employes, search]);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>

      {/* ── Colonne liste ── */}
      <div style={{ width: 320, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'white' }}>
        {/* Header */}
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h1 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)',  }}>Employés</h1>
            <button onClick={() => setShowNew(true)} data-testid="new-employe-btn"
              style={{ ...BTN_PRI, padding: '6px 12px', fontSize: '0.78rem' }}>
              <Plus size={13} /> Nouveau
            </button>
          </div>

          {/* Recherche */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8, background: 'var(--bg-subtle)' }}>
            <Search size={13} style={{ color: 'var(--text-muted)' }} />
            <input data-testid="employe-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..."
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.82rem', background: 'transparent', color: 'var(--text)' }} />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={11} /></button>}
          </div>

          {/* Filtres */}
          <div style={{ display: 'flex', gap: 6 }}>
            <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
              style={{ ...SEL, fontSize: '0.75rem', padding: '5px 8px', flex: 1 }}>
              <option value="">Tous statuts</option>
              {STATUTS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
            </select>
            <select value={filterFonction} onChange={e => setFilterFonction(e.target.value)}
              style={{ ...SEL, fontSize: '0.75rem', padding: '5px 8px', flex: 1 }}
              data-testid="filter-fonction">
              <option value="">Toutes fonctions</option>
              {FONCTIONS_INTERNES.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>

        {/* Liste */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <RefreshCw size={18} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-muted)' }}>
              <User size={28} style={{ marginBottom: 8 }} />
              <p style={{ fontSize: '0.85rem' }}>Aucun employé trouvé</p>
            </div>
          ) : (
            filtered.map(emp => {
              const nom = `${emp.personne?.prenom || ''} ${emp.personne?.nom || ''}`.trim() || emp.employe_id;
              const s = statutInfo(emp.statut);
              const isSelected = emp.employe_id === selectedId;
              const foncs = emp.fonctions_internes || [];
              return (
                <button key={emp.employe_id} data-testid={`emp-row-${emp.employe_id}`}
                  onClick={() => setSelectedId(emp.employe_id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px',
                    width: '100%', textAlign: 'left',
                    background: isSelected ? 'var(--primary-50)' : 'transparent',
                    border: 'none', borderBottom: '1px solid var(--border-light)', cursor: 'pointer',
                    borderLeft: isSelected ? '3px solid var(--primary-solid)' : '3px solid transparent',
                  }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: isSelected ? 'var(--primary-100)' : 'var(--bg-subtle)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <User size={16} style={{ color: isSelected ? 'var(--primary-solid)' : 'var(--text-muted)' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nom}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                      <StatutBadge val={emp.statut} />
                      {foncs.slice(0, 1).map(f => <FonctionBadge key={f} fonc={f} small />)}
                      {foncs.length > 1 && <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>+{foncs.length - 1}</span>}
                    </div>
                  </div>
                  <ChevronRight size={14} style={{ color: 'var(--text-disabled)', flexShrink: 0 }} />
                </button>
              );
            })
          )}
        </div>

        {/* Compteur */}
        <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
          <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
            {filtered.length} employé{filtered.length > 1 ? 's' : ''}
            {search && ` sur ${employes.length}`}
          </p>
        </div>
      </div>

      {/* ── Zone détail ── */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {selectedId ? (
          <EmployePanel key={selectedId} employeId={selectedId} onClose={() => setSelectedId(null)} onUpdated={load} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <User size={24} style={{ color: 'var(--text-disabled)' }} />
            </div>
            <p style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-muted)' }}>Sélectionnez un employé</p>
            <p style={{ fontSize: '0.78rem', marginTop: 4, color: 'var(--text-disabled)' }}>ou créez-en un nouveau</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showNew && (
        <NouvelEmployeModal
          onClose={() => setShowNew(false)}
          onCreated={async (id) => {
            setShowNew(false);
            await load();
            setSelectedId(id);
          }}
        />
      )}
    </div>
  );
}
